import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { AppointmentBanner } from "@/pages/recruiting/NewLeads";
import { ScheduleMeetingDialog } from "@/components/meetings/ScheduleMeetingDialog";
import { useAuth } from "@/context/AuthContext";
import { ActivityItem, ActivityDescription, ActivityDescriptionEditor, descriptionPayload } from "@/components/activities/ActivityRich";
import { ComposeDialog } from "@/pages/comms/Communications";
import { EmailStatusLine, ViewConversationButton } from "@/components/comms/EmailStatus";
import api, { apiError } from "@/lib/api";
import { PageLoader } from "@/components/common/Loading";
import { StatusBadge } from "@/components/common/StatusBadge";
import { UserAvatar } from "@/components/common/UserAvatar";
import { PrimeSummary } from "@/components/prime/PrimeSummary";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { EmptyState } from "@/components/common/EmptyState";
import { RecordDetailShell } from "@/components/records/RecordDetailShell";
import { RecordSections, applicableSections, DerivedGrid } from "@/components/records/RecordSections";
import { StageTracker } from "@/components/records/StageTracker";
import { RelationshipsPanel } from "@/components/crm/RelationshipsPanel";
import { PhotoAvatar } from "@/components/common/PhotoAvatar";
import { PersonSearchSelect } from "@/components/common/PersonSearchSelect";
import { PersonEditDrawer } from "@/components/people/PersonEditDrawer";
import { ReferralAttributionPanel } from "@/components/people/ReferralAttributionPanel";
import { ChangeCandidateTypeDialog } from "@/components/records/ChangeCandidateTypeDialog";
import { AppointAgentDialog } from "@/components/people/AppointAgentDialog";
import { useAcquisitionStatus, AcquisitionMenuItems, AcquisitionDatesDialog, AcquisitionOpportunityDialog } from "@/components/people/AcquisitionPersonActions";
import {
  Save, X, Trash2, MoreHorizontal, StickyNote, CheckSquare, Activity as ActIcon, Briefcase, BadgeCheck,
  Paperclip, Tag, Plus, Clock, Workflow, Download, Ban, Building2, Mail, ShieldCheck,
} from "lucide-react";
import { Eye, CalendarDays, Users, History, CalendarCheck } from "lucide-react";
import { CombinePersonDialog } from "@/components/people/CombinePersonDialog";
import { ProgramSupportPanel } from "@/components/people/ProgramSupportPanel";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { viewAttachment, downloadAttachment, viewableKind, openDocumentPreview } from "@/lib/files";
import { DocumentViewer } from "@/components/common/DocumentViewer";
import { VisibilityControls, VisibilityBadges } from "@/components/common/Visibility";
import { Switch } from "@/components/ui/switch";
import { KeyContactsPanel } from "./KeyContactsPanel";
import { SupportAssignmentsCard } from "@/components/support/SupportAssignmentsCard";
import { LifeBuoy, Presentation } from "lucide-react";
import { PersonOverview } from "@/components/people/PersonOverview";
import { OfficeStaffCard } from "@/components/people/OfficeStaffCard";
import { MilestonesTab } from "./MilestonesTab";
import { ProgramHistoryPanel } from "@/components/people/ProgramHistoryPanel";
import { MeetingDocDialog } from "@/components/meeting-docs/MeetingDocDialog";
import { TaskFormDialog } from "@/components/tasks/TaskFormDialog";
import { PersonTasksPanel } from "@/components/tasks/PersonTasksPanel";
import { PersonProjectsPanel, usePersonProjects } from "@/components/people/PersonProjectsPanel";
import { StaffAssociateTab, useAssociateEnrollment } from "@/components/associate/StaffAssociateTab";
import { toast } from "sonner";
import { TerminateDialog, ReactivateDialog } from "@/components/people/TerminateDialog";
import { PinToReport } from "@/components/common/PinToReport";
import { PersonMeetingRecords } from "@/pages/activity/MeetingRecordsTab";

function AssignedRecruiterPanel({ person, reload, canManage }) {
  const [recs, setRecs] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const orgId = person.organization_id;
  React.useEffect(() => {
    if (!orgId) { setRecs([]); return; }
    api.get(`/agency-districts/${orgId}/recruiters`).then((r) => setRecs(r.data.items || [])).catch(() => setRecs([]));
  }, [orgId]);
  if (!orgId || recs.length === 0) {
    if (!person.assigned_recruiter_name) return null;
  }
  const assign = async (uid) => {
    setSaving(true);
    try { await api.post(`/persons/${person.id}/assign-recruiter`, { recruiter_user_id: uid || null }); toast.success("Recruiter updated"); reload(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm flex items-center justify-between gap-3" data-testid="assigned-recruiter-panel">
      <div>
        <p className="text-[11px] uppercase tracking-wide text-slate-400">Assigned Recruiter (District)</p>
        <p className="text-sm font-semibold text-slate-800 mt-0.5" data-testid="assigned-recruiter-name">{person.assigned_recruiter_name || "Unassigned"}</p>
      </div>
      {canManage && recs.length > 0 && (
        <Select value={person.assigned_recruiter_id || "none"} onValueChange={(v) => assign(v === "none" ? "" : v)} disabled={saving}>
          <SelectTrigger className="h-9 w-56 text-sm" data-testid="assign-recruiter-select"><SelectValue placeholder="Assign recruiter" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="none">Unassigned</SelectItem>
            {recs.map((r) => <SelectItem key={r.user_id} value={r.user_id}>{r.name} · {r.role}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

function LinkedContactCard({ person, onBack }) {
  const rows = [["Email", person.email], ["Mobile", person.mobile || person.phone], ["Office City", person.office_city], ["Office State", person.office_state]];
  return (
    <div className="max-w-2xl mx-auto" data-testid="linked-contact-card">
      <button onClick={onBack} className="text-sm text-[#006090] mb-4" data-testid="linked-contact-back">← My Contacts</button>
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h1 className="font-display text-2xl font-semibold text-slate-900">{person.first_name} {person.last_name}</h1>
        <p className="text-xs text-slate-500 mt-1">Agency contact</p>
        <dl className="grid sm:grid-cols-2 gap-3 mt-5 text-sm">{rows.map(([k, v]) => (
          <div key={k}><dt className="text-xs text-slate-500">{k}</dt><dd className="text-slate-900">{v || "—"}</dd></div>))}</dl>
      </div>
    </div>);
}

function DirectoryToggle({ person, onSaved }) {
  const shared = !!person.agent_directory?.shared;
  const flip = async () => { try { await api.put(`/persons/${person.id}/agent-directory`, { shared: !shared }); toast.success(shared ? "Removed from the Agent Directory" : "Shared with Agents in this Group"); onSaved(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center justify-between gap-3" data-testid="agent-directory-toggle">
      <div><p className="font-semibold text-slate-900 text-sm">Agent Shared Directory</p>
        <p className="text-xs text-slate-500">{shared ? "Agents and Agent Staff in this Group can see this contact." : "Not visible to Agents."}</p></div>
      <Button size="sm" variant="outline" onClick={flip} data-testid="agent-directory-flip">{shared ? "Stop sharing" : "Share with Agents"}</Button>
    </div>);
}

export default function PersonDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isManager, isAdmin, isGroupAdmin, canRecruiting, isOfficeUser } = useAuth();
  const [combineOpen, setCombineOpen] = useState(false);
  const [appointOpen, setAppointOpen] = useState(false);
  const [acqDatesOpen, setAcqDatesOpen] = useState(false);
  const [acqOppOpen, setAcqOppOpen] = useState(false);
  const [acqStatus, setAcqStatus] = useAcquisitionStatus(id);
  const [termDlg, setTermDlg] = useState(null);

  const [person, setPerson] = useState(null);
  const [layout, setLayout] = useState(null);
  const [customFields, setCustomFields] = useState([]);
  const [candTypes, setCandTypes] = useState([]);
  const [classTypes, setClassTypes] = useState([]);
  const [presets, setPresets] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [primaryOpp, setPrimaryOpp] = useState(null);
  const [agentProfile, setAgentProfile] = useState(null);
  const [progHistory, setProgHistory] = useState(null);
  useEffect(() => {
    api.get(`/programs/enrollment-history/${id}`).then((r) => setProgHistory(r.data)).catch(() => setProgHistory(null));
  }, [id]);
  const [related, setRelated] = useState({ notes: [], tasks: [], activities: [], opps: [], attachments: [], lists: { manual: [], smart: [] }, timeline: [] });
  const [meetingRecords, setMeetingRecords] = useState([]);
  useEffect(() => {
    api.get("/activity-report/meeting-records", { params: { person_id: id } }).then((r) => setMeetingRecords(r.data.items || [])).catch(() => setMeetingRecords([]));
  }, [id]);
  const [view, setView] = useState("overview");
  const personProjects = usePersonProjects(id);
  const [assoc, setAssoc] = useAssociateEnrollment(id);
  const [protege, setProtege] = useAssociateEnrollment(id, "protege");

  const [edit, setEdit] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [form, setForm] = useState({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [ctPickOpen, setCtPickOpen] = useState(false);
  const [ctOpen, setCtOpen] = useState(null);
  const [photoKey, setPhotoKey] = useState(0);
  const [hideEmpty, setHideEmpty] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const base = [
        api.get(`/persons/${id}`),
        api.get("/layouts", { params: { record_type: "person" } }),
        api.get("/custom-fields", { params: { entity_type: "person" } }),
        api.get("/candidate-types"),
        api.get("/organizations"),
        api.get("/classification-types"),
        api.get("/layout-presets", { params: { record_type: "person" } }),
      ];
      const [p, ly, cf, ct, og, cl, pr] = await Promise.all(base);
      setPerson(p.data); setForm(p.data);
      setLayout(ly.data); setHideEmpty(ly.data.hide_empty_default !== false);
      setCustomFields(cf.data.items.filter((f) => !f.hidden));
      setCandTypes(ct.data.items); setOrgs(og.data.organizations);
      setClassTypes(cl.data.items); setPresets(pr.data.items || []);

      // attachments are readable in the recruiting workspace
      const timelineP = api.get(`/timeline/person/${id}`).then((r) => r.data.events).catch(() => []);
      if (canRecruiting) {
        const [nt, tk, ac, op, at, ls, st] = await Promise.all([
          api.get("/notes", { params: { person_id: id } }),
          api.get("/tasks", { params: { person_id: id } }),
          api.get("/activities", { params: { person_id: id } }),
          api.get("/opportunities", { params: { person_id: id, page_size: 100 } }),
          api.get("/attachments", { params: { person_id: id } }).catch(() => ({ data: { items: [] } })),
          api.get(`/persons/${id}/lists`).catch(() => ({ data: { manual: [], smart: [] } })),
          api.get("/statuses").catch(() => ({ data: { items: [] } })),
        ]);
        const opps = op.data.opportunities || [];
        setStatuses(st.data.items || []);
        setRelated({ notes: nt.data.notes, tasks: tk.data.tasks, activities: ac.data.activities, opps,
          attachments: at.data.items, lists: ls.data, timeline: await timelineP });
        if (opps.length) {
          const pick = opps.find((o) => o.id === p.data.primary_opportunity_id) || opps[0];
          const full = await api.get(`/opportunities/${pick.id}`).then((r) => r.data).catch(() => null);
          setPrimaryOpp(full);
        } else setPrimaryOpp(null);
        if ((p.data.classifications || []).includes("agent")) {
          api.get(`/persons/${id}/agent-profile`).then((r) => setAgentProfile(r.data.agent_profile)).catch(() => {});
        }
      } else {
        const [at] = await Promise.all([api.get("/attachments", { params: { person_id: id } }).catch(() => ({ data: { items: [] } }))]);
        setRelated((r) => ({ ...r, attachments: at.data.items, timeline: [] }));
        api.get(`/timeline/person/${id}`).then((r) => setRelated((prev) => ({ ...prev, timeline: r.data.events }))).catch(() => {});
      }
    } catch (e) { toast.error(apiError(e)); navigate("/people"); }
  }, [id, navigate, canRecruiting]);

  useEffect(() => { load(); }, [load]);

  // Honor ?section= from the People-list ••• menu (Create Task / Add Note open focused).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (!person) return;
    const section = searchParams.get("section");
    if (!section) return;
    if (section === "notes") setNoteOpen(true);
    else if (section === "tasks") setTaskOpen(true);
    setSearchParams({}, { replace: true });
  }, [person, searchParams, setSearchParams]);

  if (!person || !layout) return <PageLoader label="Loading person" />;
  if (person.agency_linked_contact) return <LinkedContactCard person={person} onBack={() => navigate("/people")} />;

  const ctLabel = (k) => candTypes.find((c) => c.key === k)?.label || k;
  const statusLabel = (k) => statuses.find((s) => s.key === k)?.label || k;
  const customDefs = Object.fromEntries(customFields.map((f) => [f.name, { type: f.field_type, options: f.options }]));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        first_name: form.first_name, middle_name: form.middle_name, last_name: form.last_name,
        preferred_name: form.preferred_name, email: form.email || null, alt_email: form.alt_email,
        phone: form.phone, mobile: form.mobile,
        organization_id: form.organization_id || null, source: form.source || "", source_id: form.source_id || "",
        referral_source: form.referral_source, custom_fields: form.custom_fields || {},
      };
      const { data } = await api.patch(`/persons/${id}`, payload);
      setPerson(data); setForm(data); setEdit(false); toast.success("Saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const del = async (reason) => {
    try { await api.delete(`/persons/${id}`, { data: { reason: reason || null } }); toast.success("Person moved to Deleted People"); navigate("/people"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const moveStage = async (stageId) => {
    if (!primaryOpp) return;
    try { await api.post(`/opportunities/${primaryOpp.id}/stage`, { stage_id: stageId }); toast.success("Stage updated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  // ---- assemble sections (apply matching preset, then inject unplaced custom fields) ----
  const matchedPreset = presets.find((pr) =>
    (pr.applicable_candidate_types || []).includes(primaryOpp?.candidate_type) ||
    (pr.applicable_classifications || []).some((c) => (person.classifications || []).includes(c)));
  const activeLayout = matchedPreset || layout;
  let sections = [...(activeLayout.sections || [])];
  const placed = new Set();
  sections.forEach((s) => (s.fields || []).forEach((f) => f.source === "custom" && placed.add(f.key)));
  const unplaced = customFields.filter((f) => !placed.has(f.name));
  if (unplaced.length) {
    sections = [...sections, { key: "additional_information", title: "Additional Information", render: "fields",
      columns: 2, collapsible: true, conditional: {}, order: 999,
      fields: unplaced.map((f) => ({ key: f.name, label: f.label, source: "custom", type: f.field_type, visible: true, width: "half" })) }];
  }
  const shown = applicableSections(sections, {
    hasOpportunity: !!primaryOpp, candidateType: primaryOpp?.candidate_type,
    classifications: person.classifications || [], canRecruiting,
  }).filter((s) => !["important_dates", "recruiting_summary", "relationships"].includes(s.render));

  const systemRenderers = {
    recruiting_summary: () => (
      <DerivedGrid testId="recruiting-summary-grid" rows={[
        ["Candidate Type", ctLabel(primaryOpp?.candidate_type)],
        ["Pipeline", primaryOpp ? (primaryOpp.pipeline_name || primaryOpp.pipeline?.name || "Unknown") : null],
        ["Current Stage", primaryOpp?.current_stage_name],
        ["Opportunity Status", statusLabel(primaryOpp?.status)],
        ["Interest Level", primaryOpp?.interest_level],
        ["Priority", primaryOpp?.priority],
        ["Assigned Recruiter", primaryOpp?.recruiter_name],
        ["Organization", primaryOpp?.organization_name],
        ["Source", primaryOpp?.source],
        ["Opportunity Opened", primaryOpp?.open_date ? new Date(primaryOpp.open_date).toLocaleDateString() : null],
        ["Last Activity", primaryOpp?.last_activity_at ? new Date(primaryOpp.last_activity_at).toLocaleString() : null],
        ["Next Action", primaryOpp?.next_activity_at ? new Date(primaryOpp.next_activity_at).toLocaleString() : null],
      ]} />
    ),
    important_dates: () => (
      <DerivedGrid testId="important-dates-grid" rows={[
        ["Person Added", person.created_at ? new Date(person.created_at).toLocaleDateString() : null],
        ...((primaryOpp?.stage_history || []).map((h) => [`${h.stage_name} Date`, h.entered_at ? new Date(h.entered_at).toLocaleDateString() : null])),
        ["Last Contact", person.last_activity_at ? new Date(person.last_activity_at).toLocaleDateString() : null],
        ["Next Follow-Up", primaryOpp?.next_activity_at ? new Date(primaryOpp.next_activity_at).toLocaleDateString() : null],
      ]} />
    ),
    agent_profile: () => {
      const cf = person.custom_fields || {};
      const cls = person.classifications || [];
      const isStaff = cls.includes("agent_staff") && !cls.includes("agent");
      const code = agentProfile?.agent_number || cf.agent_number;
      return (
      <DerivedGrid testId="agent-profile-grid" rows={[
        [isStaff ? "Affiliated Agency Code" : "Agent Code", code],
        ["Agency / Business", agentProfile?.agency_name],
        ["Agency Model", agentProfile?.agent_type],
        ["Career Date", agentProfile?.career_date ? new Date(agentProfile.career_date).toLocaleDateString() : null],
        ["Active", agentProfile ? (agentProfile.active ? "Yes" : "No") : null],
      ]} />
      );
    },
    relationships: () => <RelationshipsPanel bare recordType="person" recordId={id} />,
  };

  // ---- header ----
  const handleRemoveDnc = async () => {
    const reason = window.prompt("Confirm: lift the Do Not Contact restriction for this Person. Optional reason:");
    if (reason === null) return;
    try { await api.post(`/persons/${id}/remove-dnc`, { reason: reason || null }); toast.success("Do Not Contact restriction removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const handleConvertBiz = async () => {
    if (!window.confirm("Convert this record to an Agency/Business and archive the Person? Provenance is preserved.")) return;
    try { const { data } = await api.post(`/persons/${id}/convert-to-business`, {}); toast.success(`Converted to Business: ${data.name}`); window.location.href = "/people"; }
    catch (e) { toast.error(apiError(e)); }
  };
  const pcls = person.classifications || [];
  const isAgentProfile = (pcls.includes("agent") || pcls.includes("agent_staff")) && !pcls.includes("candidate");
  const header = (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3.5 shadow-sm" data-testid="person-header">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <PhotoAvatar personId={id} name={`${person.first_name} ${person.last_name}`} size={48} hasPhoto={!!person.photo_path} refreshKey={photoKey} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h1 className="font-display text-xl font-bold text-slate-900" data-testid="person-name">
              {person.first_name} {person.last_name}
              {person.preferred_name && <span className="text-slate-400 font-normal text-base"> ({person.preferred_name})</span>}
            </h1>
            <span className="text-xs text-slate-400 font-mono">{person.human_id} · {person.organization_name || "Unassigned"}</span>
            <PinToReport type="person" id={id} />
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5" data-testid="person-header-badges">
            {person.do_not_contact && (
              <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-700 border border-rose-300 px-2.5 py-0.5 text-xs font-semibold" data-testid="dnc-badge" title="Communication suppressed">
                <Ban className="w-3.5 h-3.5" /> Do Not Contact
              </span>
            )}
            {(person.classifications || []).filter((c) => !(c === "terminated" && person.status === "terminated")).map((c) => <StatusBadge key={c} status={c} />)}
            {person.status === "terminated" && (
              <span className="inline-flex items-center rounded-full bg-slate-800 text-white px-2.5 py-0.5 text-xs font-bold tracking-wide" data-testid="terminated-badge"
                title={`Terminated ${person.termination?.date || person.termination_date || ""} · ${person.termination?.reason || ""}`}>
                TERMINATED {person.termination?.date || person.termination_date || ""}{person.termination?.reason ? ` · ${person.termination.reason}` : ""}
              </span>)}
            {person.can_terminate && <Link to={`/admin/people?tab=${person.status === "terminated" ? "terminated" : "active"}&q=${encodeURIComponent(person.last_name || "")}`} className="text-xs text-slate-500 underline" data-testid="person-manage-lifecycle">Manage status in Admin Center → People</Link>}
            {termDlg === "terminate" && <TerminateDialog personId={id} onClose={() => setTermDlg(null)} onDone={(s) => { setTermDlg(null); toast.message(s); load(); }} />}
            {termDlg === "reactivate" && <ReactivateDialog personId={id} onClose={() => setTermDlg(null)} onDone={(s) => { setTermDlg(null); toast.message(s); load(); }} />}
            {!primaryOpp && !isAgentProfile && person.candidate_type_labels?.length > 0 && <StatusBadge status="producer" label={person.candidate_type_labels.join(" / ")} />}
            {primaryOpp && (
              <>
                <span className="h-4 w-px bg-slate-200 mx-1" />
                {isAgentProfile ? null : person.candidate_type_labels?.length > 0 ? <StatusBadge status="producer" label={`${person.candidate_type_labels.join(" / ")}${person.candidate_type_conflict ? " ⚠" : ""}`} />
                  : primaryOpp.candidate_type && <StatusBadge status="producer" label={ctLabel(primaryOpp.candidate_type)} />}
                {primaryOpp.current_stage_name && <span className="text-xs rounded-full bg-brand/10 text-brand px-2.5 py-0.5 font-medium">{primaryOpp.current_stage_name}</span>}
                <StatusBadge status={primaryOpp.status} label={statusLabel(primaryOpp.status)} />
              </>
            )}
          </div>
          {isAgentProfile && <PrimeSummary personId={id} />}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-sm" data-testid="person-header-contact">
            {person.email ? <a href={`mailto:${person.email}`} className="text-[#006090] hover:underline break-all" data-testid="person-header-email">{person.email}</a> : <span className="text-slate-400">No email on file</span>}
            {(person.mobile || person.phone) ? <a href={`tel:${(person.mobile || person.phone).replace(/[^0-9+]/g, "")}`} className="text-[#006090] hover:underline" data-testid="person-header-mobile">{person.mobile || person.phone}</a> : <span className="text-slate-400">No mobile on file</span>}
            {!isOfficeUser && <span className="text-xs text-slate-500" data-testid="person-header-recruiter">Recruiter: <b className="font-medium text-slate-700">{primaryOpp?.recruiter_name || person.assigned_recruiter_name || "Unassigned"}</b></span>}
            <span className="text-[11px] text-slate-400" title={person.updated_at ? new Date(person.updated_at).toLocaleString() : ""}>Updated {person.updated_at ? new Date(person.updated_at).toLocaleDateString() : "—"}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => setDrawerOpen(true)} data-testid="edit-person">Edit</Button>
          {canRecruiting && person?.email && <Button variant="outline" onClick={() => setComposeOpen(true)} data-testid="header-email"><Mail className="w-4 h-4 mr-1.5" /> Email</Button>}
          {canRecruiting && <Button variant="outline" onClick={() => setNoteOpen(true)} data-testid="header-add-note"><StickyNote className="w-4 h-4 mr-1.5" /> Add Note</Button>}
          {canRecruiting && <Button variant="outline" onClick={() => setTaskOpen(true)} data-testid="header-add-task"><CheckSquare className="w-4 h-4 mr-1.5" /> Create Task</Button>}
          {(canRecruiting || isManager || isAdmin || isGroupAdmin) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="outline" size="icon" data-testid="person-more" aria-label="More actions"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white">
                {(canRecruiting || isManager || isAdmin || isGroupAdmin) && !isOfficeUser && <DropdownMenuItem onClick={() => navigate(`/present/agency-ownership/${person.id}`)} data-testid="present-agency-ownership"><Presentation className="w-4 h-4 mr-2" /> Present Agency Ownership</DropdownMenuItem>}
                <AcquisitionMenuItems personId={person.id} status={acqStatus} onDates={() => setAcqDatesOpen(true)} onOpportunity={() => setAcqOppOpen(true)} />
                {(isManager || isAdmin || isGroupAdmin) && !(person.classifications || []).includes("agent") && <DropdownMenuItem onClick={() => setAppointOpen(true)} data-testid="appoint-agent"><BadgeCheck className="w-4 h-4 mr-2" /> Appoint as Agent</DropdownMenuItem>}
                {canRecruiting && primaryOpp && <DropdownMenuItem onClick={() => setCtPickOpen(true)} data-testid="header-change-ct"><Workflow className="w-4 h-4 mr-2" /> Change Candidate Type</DropdownMenuItem>}
                {(isAdmin || isGroupAdmin) && <DropdownMenuItem onClick={() => setCombineOpen(true)} data-testid="combine-person"><Users className="w-4 h-4 mr-2" /> Combine with…</DropdownMenuItem>}
                {(isManager || isAdmin) && <DropdownMenuItem onClick={() => setConfirmDel(true)} className="text-rose-600" data-testid="delete-person"><Trash2 className="w-4 h-4 mr-2" /> Move to Deleted People</DropdownMenuItem>}
                {person.do_not_contact && <DropdownMenuItem onClick={handleRemoveDnc} data-testid="remove-dnc"><Ban className="w-4 h-4 mr-2" /> Remove Do Not Contact Restriction</DropdownMenuItem>}
                {person.data_quality_flag === "possible_non_person_entity" && <DropdownMenuItem onClick={handleConvertBiz} data-testid="convert-business"><Building2 className="w-4 h-4 mr-2" /> Convert to Agency / Business</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </div>
  );

  const isActiveCandidate = progHistory ? progHistory.active_candidate
    : (person.classifications || []).includes("candidate") || related.opps.some((o) => ["active", "nurturing"].includes(o.status));
  const overview = (
    <>
      {composeOpen && <ComposeDialog personId={person.id} onClose={() => setComposeOpen(false)} />}
      <PersonOverview
        person={person} opps={related.opps} tasks={related.tasks} timeline={related.timeline}
        notes={related.notes} activities={related.activities} ctLabel={ctLabel}
        attachments={related.attachments} agentProfile={agentProfile} primaryOpp={primaryOpp}
        canManage={canRecruiting} onCreateTask={canRecruiting ? () => setTaskOpen(true) : undefined}
        onReload={load} onNavigate={setView} activeCandidate={isActiveCandidate}
        stageTracker={primaryOpp ? <StageTracker stages={primaryOpp.pipeline?.stages || []} currentStageId={primaryOpp.current_stage_id} onMove={canRecruiting ? moveStage : undefined}
          enteredAt={(() => { const h = [...(primaryOpp.stage_history || [])].reverse().find((x) => x.stage_id === primaryOpp.current_stage_id); return h ? (h.effective_date || h.entered_at) : null; })()} /> : null}
        keyInformation={
          <RecordSections sections={shown} record={person} custom={person.custom_fields} form={form} setForm={setForm}
            customDefs={customDefs} orgs={orgs} edit={false} hideEmpty={hideEmpty} systemRenderers={systemRenderers} />
        }
        supportContacts={<div className="space-y-4">{isGroupAdmin && !isOfficeUser && <DirectoryToggle person={person} onSaved={load} />}{((person.classifications || []).includes("agent") || person.classification === "agent") && <OfficeStaffCard personId={person.id} />}<AssignedRecruiterPanel person={person} reload={load} canManage={canRecruiting} />{canRecruiting && <ReferralAttributionPanel personId={person.id} />}{canRecruiting && <ProgramSupportPanel personId={person.id} />}<KeyContactsPanel personId={person.id} organizationId={person.organization_id} canManage={canRecruiting} /></div>}
      />
    </>
  );

  const timeline = <TimelinePanel events={related.timeline} />;
  const openTaskCount = related.tasks.filter((t) => !["completed", "cancelled", "not_applicable"].includes(t.status) && !t.archived && t.status !== "planned").length;

  const pastPrograms = progHistory?.history || [];
  const quickExtra = [];
  if (canRecruiting && isActiveCandidate) quickExtra.push({ key: "milestones", label: "Milestones", icon: Workflow,
    node: <MilestonesTab personId={id} canManage={canRecruiting} tasks={related.tasks} onOpenLicensing={() => setView("licenses")} onTasksChanged={load} /> });
  if (canRecruiting && pastPrograms.length > 0) quickExtra.push({ key: "program-history", label: "Candidate Program History", icon: History,
    count: pastPrograms.length, node: <ProgramHistoryPanel personId={id} items={pastPrograms} /> });
  const relatedRecords = [];
  const moreItems = [];
  if (canRecruiting) {
    relatedRecords.push(
      { key: "tasks", label: "Tasks", icon: CheckSquare, count: openTaskCount, countTitle: `${openTaskCount} open of ${related.tasks.length} total`,
        node: <PersonTasksPanel personId={id} personName={[person.first_name, person.last_name].filter(Boolean).join(" ")} items={related.tasks} reload={load} Card={PanelCard} /> },
      { key: "projects", label: "Projects", icon: Briefcase, count: personProjects.length,
        node: <PersonProjectsPanel items={personProjects} /> },
      { key: "meetings", label: "Meetings", icon: CalendarDays, count: person.meetings_count || 0,
        node: <MeetingsPanel personId={id} linkedUser={person.linked_user} /> },
      { key: "activities", label: "Activities", icon: ActIcon, count: related.activities.length,
        node: <ActivitiesWithTimeline activities={<ActivitiesPanel personId={id} items={related.activities} reload={load} />} timeline={timeline} /> },
      { key: "notes", label: "Notes", icon: StickyNote, count: related.notes.length,
        node: <NotesPanel personId={id} items={related.notes} reload={load}
          who={(person?.classifications || []).includes("candidate") ? "Candidate" : (person?.classifications || []).includes("agent") ? "Agent" : "Person"} /> },
    );
  }
  relatedRecords.push({ key: "attachments", label: "Documents", icon: Paperclip, count: related.attachments.length,
    node: <AttachmentsPanel personId={id} items={related.attachments} canManage={isManager || isAdmin} canUpload={canRecruiting} onChanged={load}
      who={(person?.classifications || []).includes("candidate") ? "Candidate" : (person?.classifications || []).includes("agent") ? "Agent" : "Person"} /> });
  if (meetingRecords.length > 0) relatedRecords.push({ key: "meeting-records", label: "Meeting Records", icon: Paperclip, count: meetingRecords.length,
    node: <PersonMeetingRecords items={meetingRecords} /> });
  if (canRecruiting) {
    relatedRecords.push({ key: "opportunities", label: "Support", icon: LifeBuoy,
      node: <div className="space-y-4" data-testid="panel-support"><SupportAssignmentsCard personId={id} /><OpportunitiesPanel opps={related.opps} personId={id} navigate={navigate} reload={load} /></div> });
    moreItems.push(
      { key: "licenses", label: "Licensing & Compliance", icon: ShieldCheck, node: <LicensesPanel personId={id} canManage={canRecruiting} /> },
      ...(assoc?.enrolled && isActiveCandidate ? [{ key: "associate", label: "Associate Program", icon: Workflow, node: <StaffAssociateTab data={assoc} setData={setAssoc} /> }] : []),
      ...(protege?.enrolled ? [{ key: "protege", label: "Protégé Program", icon: Workflow, node: <StaffAssociateTab data={protege} setData={setProtege} /> }] : []),
      { key: "lists", label: "Lists", icon: Tag, count: (related.lists.manual?.length || 0) + (related.lists.smart?.length || 0), node: <ListsPanel lists={related.lists} /> },
    );
  }
  if (isAdmin) moreItems.push({ key: "history", label: "Record History", icon: History, node: <RecordHistoryPanel personId={id} /> });

  return (
    <>
      <AppointmentBanner appointmentId={searchParams.get("appointment")} />
      <RecordDetailShell
        backLabel={isOfficeUser ? "My Contacts" : "People"} onBack={() => navigate("/people")}
        header={header}
        overview={overview}
        view={view} onViewChange={setView}
        quickExtra={quickExtra}
        related={relatedRecords}
        moreItems={moreItems}
        hideEmpty={hideEmpty} onToggleHideEmpty={setHideEmpty} showHideEmpty={false}
      />
      <DeletePersonDialog open={confirmDel} onOpenChange={setConfirmDel} onConfirm={del} />
      {acqOppOpen && <AcquisitionOpportunityDialog personId={person.id} status={acqStatus} onClose={() => setAcqOppOpen(false)} onSaved={() => { setAcqOppOpen(false); api.get(`/acquisition/persons/${person.id}`).then((r) => setAcqStatus(r.data)); }} />}
      {acqDatesOpen && <AcquisitionDatesDialog personId={person.id} status={acqStatus} onClose={() => setAcqDatesOpen(false)} onSaved={() => { setAcqDatesOpen(false); api.get(`/acquisition/persons/${person.id}`).then((r) => setAcqStatus(r.data)); }} />}
      {appointOpen && <AppointAgentDialog personId={person.id} onClose={() => setAppointOpen(false)} onDone={() => { setAppointOpen(false); load(); }} />}
      {combineOpen && <CombinePersonDialog person={person} classTypes={classTypes} onClose={() => setCombineOpen(false)}
        onDone={(sid) => { setCombineOpen(false); if (sid === person.id) { load(); } else { navigate(`/people/detail/${sid}`); } }} />}
      {noteOpen && <AddNoteDialog personId={id} onClose={() => setNoteOpen(false)} onDone={() => { setNoteOpen(false); load(); }} />}
      {taskOpen && <TaskFormDialog open onClose={() => setTaskOpen(false)} onCreated={() => { setTaskOpen(false); load(); }} defaults={{ person_id: id }} lockPerson title="New Task" />}
      {drawerOpen && <PersonEditDrawer person={person} orgs={orgs} classTypes={classTypes} candTypes={candTypes} primaryOpp={primaryOpp}
        onClose={() => setDrawerOpen(false)} onSaved={() => { setDrawerOpen(false); setPhotoKey((k) => k + 1); load(); }}
        onChangeCandidateType={() => { setDrawerOpen(false); setCtPickOpen(true); }}
        onPhotoChanged={(has) => { setPerson((p) => ({ ...p, photo_path: has ? "set" : null })); setPhotoKey((k) => k + 1); }} />}
      {ctPickOpen && <CandidateTypePicker candTypes={candTypes} current={primaryOpp?.candidate_type}
        onClose={() => setCtPickOpen(false)} onPick={(k) => { setCtPickOpen(false); setCtOpen({ candidate_type: k }); }} />}
      {ctOpen && primaryOpp && <ChangeCandidateTypeDialog oid={primaryOpp.id} target={ctOpen.candidate_type} current={primaryOpp}
        ctLabel={ctLabel} onClose={() => setCtOpen(null)} onDone={() => { setCtOpen(null); load(); }} />}
    </>
  );
}

function CandidateTypePicker({ candTypes, current, onClose, onPick }) {
  const [val, setVal] = useState("");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="candidate-type-picker">
        <DialogHeader><DialogTitle>Change Candidate Type</DialogTitle>
          <DialogDescription>Select the new Candidate Type for this person's active Recruiting Opportunity.</DialogDescription></DialogHeader>
        <Select value={val} onValueChange={setVal}>
          <SelectTrigger data-testid="ct-pick-select"><SelectValue placeholder="Select candidate type" /></SelectTrigger>
          <SelectContent className="bg-white max-h-72">{candTypes.filter((c) => c.key !== current).map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
        </Select>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => val && onPick(val)} disabled={!val} data-testid="ct-pick-next" className="bg-brand hover:bg-brand-strong">Next</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const DELETE_REASONS = ["Duplicate Record", "Entered in Error", "No Longer Needed", "Other"];
function DeletePersonDialog({ open, onOpenChange, onConfirm }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const finalReason = reason === "Other" ? (note.trim() || "Other") : (reason ? (note.trim() ? `${reason} — ${note.trim()}` : reason) : (note.trim() || null));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid="delete-person-dialog">
        <DialogHeader><DialogTitle>Move this person to Deleted People?</DialogTitle>
          <DialogDescription>This person will be moved to Deleted People and can be restored later with the same Person ID and all related data.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold text-slate-500">Reason for deletion (optional)</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="mt-1" data-testid="delete-reason-select"><SelectValue placeholder="Select a reason" /></SelectTrigger>
              <SelectContent className="bg-white">{DELETE_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-500">Notes (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" placeholder="Add context…" data-testid="delete-reason-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => { onConfirm(finalReason); onOpenChange(false); }} data-testid="delete-confirm" className="bg-rose-600 hover:bg-rose-700">Move to Deleted People</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- related panels ---------------- */
function MeetingsPanel({ personId, linkedUser }) {
  const { user, isSuper } = useAuth();
  const [items, setItems] = useState(null);
  const [docs, setDocs] = useState([]);
  const [viewDoc, setViewDoc] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [status, setStatus] = useState(linkedUser?.status || null);
  const [outcomeFor, setOutcomeFor] = useState(null);
  const [schedOpen, setSchedOpen] = useState(false);
  const nav = useNavigate();
  const { viewAs } = useAuth();
  const [pinfo, setPinfo] = useState(null);
  useEffect(() => { api.get(`/persons/${personId}`).then((r) => setPinfo(r.data.person || r.data)).catch(() => {}); }, [personId]);
  const loadM = () => api.get(`/persons/${personId}/meetings`).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  const clearOutcome = (m) => clearOutcomeRequest(m, loadM);
  const loadDocs = () => api.get("/meeting-docs", { params: { person_id: personId } }).then((r) => setDocs(r.data.items || [])).catch(() => setDocs([]));
  useEffect(() => { loadM(); loadDocs(); }, [personId]);
  const invite = async () => {
    setInviting(true);
    try { const { data } = await api.post(`/persons/${personId}/portal-invite`); setStatus(data.status === "invited" ? "invited" : data.status); if (data.invited) toast.success("Portal invitation sent"); else if (data.status === "conflict" || data.status === "no_email") toast.error(data.message); else toast(data.message || `Portal account: ${data.status}`); }
    catch (e) { toast.error(apiError(e)); } finally { setInviting(false); }
  };
  const portalLabel = status === "active" ? "Active" : status === "invited" ? "Invitation Pending" : status === "disabled" ? "Disabled" : "Not Invited";
  const OUTCOME_STYLES = {
    "Strong": "bg-emerald-100 text-emerald-700", "Follow-up": "bg-amber-100 text-amber-700",
    "Not a fit": "bg-slate-200 text-slate-600", "No-show": "bg-rose-100 text-rose-600",
  };
  return (
    <PanelCard title="Meetings" testId="panel-meetings"
      action={<div className="flex items-center gap-2" data-testid="portal-account-status">
        <span className="text-xs text-slate-500">Candidate Portal: <b className="text-slate-700">{portalLabel}</b></span>
        {status !== "active" && status !== "disabled" && (
          <Button size="sm" variant="outline" disabled={inviting} onClick={invite} data-testid="portal-invite-btn">
            {status === "invited" ? "Resend Invite" : "Invite"}
          </Button>
        )}
      </div>}>
      {!viewAs && <div className="flex flex-wrap gap-2 mb-3" data-testid="meetings-actions">
        <Button size="sm" className="bg-brand hover:bg-brand-strong" onClick={() => setSchedOpen(true)} data-testid="meetings-schedule-btn">Schedule Meeting</Button>
        <Button size="sm" variant="outline" onClick={() => nav(`/calendar/document-meeting?person_id=${personId}`)} data-testid="meetings-document-btn">Document Completed Meeting</Button>
      </div>}
      {schedOpen && pinfo && <ScheduleMeetingDialog open person={pinfo} onClose={() => setSchedOpen(false)} onSaved={loadM} />}
      {items === null ? <p className="text-sm text-slate-400 py-6 text-center">Loading…</p>
        : items.length === 0 ? <EmptyState icon={CalendarDays} title="No meetings" description="Schedule a meeting or document one that already happened." />
        : <div className="space-y-2" data-testid="meetings-list">
            {items.map((m) => {
              const canLog = (m.can_manage_outcome || m.host_id === user?.id || isSuper) && m.status !== "cancelled";
              return (
              <div key={m.id} className="rounded-lg border border-slate-200 p-3 text-sm" data-testid={`meeting-${m.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-slate-800">{m.appointment_type}{m.is_interview && <span className="ml-2 text-[10px] uppercase font-bold text-[#006090]">Interview</span>}{m.source === "documented" && <span className="ml-2 text-[10px] text-slate-500">Documented</span>}</span>
                  <div className="flex items-center gap-2">
                    {m.in_outlook && <span className="inline-flex items-center gap-1 text-[11px] text-slate-500" title="Linked to the host's Outlook calendar event" data-testid={`meeting-in-outlook-${m.id}`}><CalendarCheck className="h-3 w-3 text-[#006090]" />In Outlook</span>}
                    {m.outcome_label
                      ? <span className={`text-xs px-2 py-0.5 rounded-full ${m.outcome_color ? "" : OUTCOME_STYLES[m.outcome_label] || "bg-slate-100 text-slate-600"}`} style={m.outcome_color ? { backgroundColor: `${m.outcome_color}22`, color: m.outcome_color } : undefined} data-testid={`meeting-outcome-${m.id}`}>{m.outcome_label}</span>
                      : <span className={`text-xs px-2 py-0.5 rounded-full ${m.report_status === "cancelled" || m.report_status === "no_show" ? "bg-rose-100 text-rose-600" : m.report_status === "completed" ? "bg-emerald-100 text-emerald-700" : m.report_status === "awaiting_outcome" ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sky-700"}`} data-testid={`meeting-status-${m.id}`}>{(m.report_status || m.status || "").replace("_", " ")}</span>}
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-1">{m.start ? new Date(m.start).toLocaleString() : ""} · {m.timezone || ""}</p>
                <p className="text-xs text-slate-500">Host: {m.host_name || "—"}{m.booked_by ? ` · Booked by: ${m.booked_by}` : ""} · {(m.mode || "").replace("_", " ")}</p>
                {m.meeting_link ? <a href={m.meeting_link} target="_blank" rel="noreferrer" className="text-xs text-brand underline">{m.meeting_link.includes("teams.microsoft") ? "Teams link" : "Meeting link"}</a> : null}
                {m.outcome_notes ? <p className="text-xs text-slate-600 mt-1.5 italic border-l-2 border-slate-200 pl-2">{m.outcome_notes}</p> : null}
                {m.outcome_logged_by_name ? <p className="text-[11px] text-slate-400 mt-1">Logged by {m.outcome_logged_by_name}{m.outcome_logged_at ? ` · ${new Date(m.outcome_logged_at).toLocaleDateString()}` : ""}</p> : null}
                {(m.meeting_doc_id || canLog) && (
                  <div className="mt-2 flex justify-end gap-2">
                    {m.meeting_doc_id && (
                      <Button size="sm" variant="outline" onClick={() => setViewDoc(m.meeting_doc_id)} data-testid={`meeting-view-notes-${m.id}`}>
                        <StickyNote className="w-3.5 h-3.5 mr-1" /> View Meeting Notes
                      </Button>
                    )}
                    {canLog && (
                      <Button size="sm" variant={m.outcome_label ? "ghost" : "outline"} onClick={() => setOutcomeFor(m)} data-testid={`meeting-log-outcome-${m.id}`}>
                        {m.outcome_label ? "Update Outcome" : "Log Outcome"}
                      </Button>
                    )}
                    {canLog && m.outcome_label && (
                      <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => clearOutcome(m)} data-testid={`meeting-clear-outcome-${m.id}`}>Clear Outcome</Button>
                    )}
                  </div>
                )}
              </div>
            );})}
          </div>}
      {docs.length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-100">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Documented Meetings</p>
          <div className="space-y-2" data-testid="documented-meetings-list">
            {docs.map((d) => (
              <button key={d.id} type="button" onClick={() => setViewDoc(d.id)} data-testid={`documented-meeting-${d.id}`}
                className="w-full text-left rounded-lg border border-slate-200 p-3 hover:bg-slate-50 transition-colors">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-slate-800">{d.title}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-brand/10 text-brand">{d.meeting_type_label}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {d.meeting_date ? new Date(d.meeting_date + "T00:00:00").toLocaleDateString() : ""}
                  {d.meeting_time ? ` · ${d.meeting_time}` : ""} · {(d.attendees || []).length} attendee{(d.attendees || []).length === 1 ? "" : "s"} · {d.created_by_name || "—"}
                </p>
                <span className="text-xs text-brand font-medium mt-1 inline-block">View Meeting Notes →</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {outcomeFor && <OutcomeDialog meeting={outcomeFor} onClose={() => setOutcomeFor(null)} onSaved={() => { setOutcomeFor(null); loadM(); }} />}
      {viewDoc && <MeetingDocDialog docId={viewDoc} open onClose={() => setViewDoc(null)} onChanged={() => { loadDocs(); loadM(); }} />}
    </PanelCard>
  );
}

const OUTCOME_OPTIONS = [];

export const clearOutcomeRequest = async (m, after) => {
  if (!window.confirm(`Clear the outcome "${m.outcome_label}" for this meeting? It stays in the audit history.`)) return;
  try { await api.delete(`/meetings/${m.id}/outcome`); toast.success("Outcome cleared"); after(); } catch (e) { toast.error(apiError(e)); }
};

export function OutcomeDialog({ meeting, onClose, onSaved }) {
  const [outcome, setOutcome] = useState(meeting.outcome || "");
  const [cats, setCats] = useState(OUTCOME_OPTIONS);
  useEffect(() => { api.get("/interview-outcomes").then((r) => setCats(r.data.items)).catch(() => setCats([])); }, []);
  const offersFollowUp = !!cats.find((c) => c.code === outcome)?.offers_follow_up;
  const [notes, setNotes] = useState(meeting.outcome_notes || "");
  const [makeTask, setMakeTask] = useState(false);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!outcome) { toast.error("Select an outcome"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/meetings/${meeting.id}/outcome`, {
        outcome, notes, create_follow_up_task: offersFollowUp ? makeTask : false });
      toast.success(data?.follow_up_task_id ? "Outcome logged · follow-up task created" : "Outcome logged");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md bg-white" data-testid="outcome-dialog">
        <DialogHeader>
          <DialogTitle>Log Interview Outcome</DialogTitle>
          <DialogDescription>{meeting.appointment_type}{meeting.start ? ` · ${new Date(meeting.start).toLocaleString()}` : ""}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label className="text-sm">Outcome</Label>
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger data-testid="outcome-select" className="mt-1"><SelectValue placeholder="Select outcome" /></SelectTrigger>
              <SelectContent className="bg-white">
                {cats.map((o) => <SelectItem key={o.code} value={o.code} data-testid={`outcome-opt-${o.code}`}><span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: o.color }} />{o.label}</span></SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Notes</Label>
            <Textarea className="mt-1" rows={4} placeholder="Interviewer notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="outcome-notes" />
          </div>
          {offersFollowUp && (
            <label className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 cursor-pointer" data-testid="outcome-followup-task-row">
              <input type="checkbox" className="mt-0.5 h-4 w-4 accent-amber-600" checked={makeTask}
                onChange={(e) => setMakeTask(e.target.checked)} data-testid="outcome-create-task" />
              <span className="text-sm text-slate-700">Create a follow-up task for me, due in <b>one week</b></span>
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="outcome-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-strong" data-testid="outcome-save">{saving ? "Saving…" : "Save Outcome"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function PanelCard({ title, action, children, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-semibold text-slate-900 text-sm">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

const LIC_SOURCE_LABEL = { candidate_background_request: "Candidate Submitted", staff_entered: "Staff Entered",
  imported: "Imported", system: "System / Integration" };

function LicensesPanel({ personId, canManage }) {
  const [licenses, setLicenses] = useState(null);
  const [dialog, setDialog] = useState(null); // {mode:'add'|'edit', license}
  const load = useCallback(() => {
    api.get(`/persons/${personId}/licenses`).then((r) => setLicenses(r.data.licenses || [])).catch(() => setLicenses([]));
  }, [personId]);
  useEffect(() => { load(); }, [load]);
  const BADGE = { verified: "active", rejected: "inactive", unverified: "in_review" };
  const LABEL = { verified: "Verified", rejected: "Not Accepted", unverified: "Unverified" };
  const archive = async (l) => {
    try { await api.post(`/persons/${personId}/licenses/${l.id}/archive`); toast.success("License archived"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <PanelCard title="Insurance Licenses" testId="panel-licenses"
      action={canManage ? <Button size="sm" onClick={() => setDialog({ mode: "add" })} data-testid="license-add" className="bg-brand hover:bg-brand-strong"><Plus className="w-4 h-4 mr-1" /> Add License</Button> : null}>
      {licenses === null ? <p className="text-sm text-slate-400">Loading…</p> : licenses.length === 0 ? (
        <EmptyState icon={ShieldCheck} title="No licenses on file"
          description="Add licensing information manually or collect it through the candidate approval process." />
      ) : (
        <div className="space-y-2">
          {licenses.map((l) => (
            <div key={l.id} className="flex items-center justify-between gap-3 border border-slate-100 rounded-lg p-3" data-testid={`person-license-${l.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800">{l.line} · {l.state}{l.license_number ? ` · #${l.license_number}` : ""}{l.license_type ? ` · ${l.license_type}` : ""}</p>
                <p className="text-xs text-slate-400">
                  {(l.status || (l.active ? "Active" : "Inactive"))}{l.resident ? ` · ${l.resident === "non_resident" ? "Non-Resident" : "Resident"}` : ""}{l.expiration ? ` · exp ${l.expiration}` : ""}
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">Source: {LIC_SOURCE_LABEL[l.source] || "—"}{l.possible_duplicate_of ? " · Possible Existing License" : ""}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <StatusBadge status={BADGE[l.verification_status] || "in_review"} label={LABEL[l.verification_status] || l.verification_status} />
                {canManage && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`license-menu-${l.id}`}><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-white">
                      <DropdownMenuItem onClick={() => setDialog({ mode: "edit", license: l })} data-testid={`license-edit-${l.id}`}>Edit License</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => archive(l)} data-testid={`license-archive-${l.id}`}>Archive License</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {dialog && <LicenseDialog personId={personId} mode={dialog.mode} license={dialog.license}
        onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); }} />}
    </PanelCard>
  );
}

function LicenseDialog({ personId, mode, license, onClose, onDone }) {
  const [f, setF] = useState(() => ({ line: "", state: "", license_number: "", license_type: "",
    resident: "", status: "active", expiration: "", issue_date: "", verification_status: "unverified",
    notes: "", ...(license || {}) }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.line.trim() || !f.state.trim()) return toast.error("Line of authority and State are required");
    setSaving(true);
    try {
      const body = { line: f.line.trim(), state: f.state.trim(), license_number: f.license_number || null,
        license_type: f.license_type || null, resident: f.resident || null, status: f.status || null,
        issue_date: f.issue_date || null, expiration: f.expiration || null,
        verification_status: f.verification_status, notes: f.notes || null };
      if (mode === "add") {
        const { data } = await api.post(`/persons/${personId}/licenses`, body);
        if (data.duplicate_warning) toast.warning(data.duplicate_warning);
        toast.success("License added");
      } else {
        await api.patch(`/persons/${personId}/licenses/${license.id}`, body);
        toast.success("License updated");
      }
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="license-dialog">
        <DialogHeader><DialogTitle>{mode === "add" ? "Add License" : "Edit License"}</DialogTitle>
          <DialogDescription>Insurance license record for this person.</DialogDescription></DialogHeader>
        <div className="space-y-3 max-h-[62vh] overflow-y-auto tr-scroll pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold text-slate-700">Line(s) of Authority *</Label>
              <Input value={f.line} onChange={(e) => set("line", e.target.value)} placeholder="Property & Casualty" data-testid="license-line" /></div>
            <div><Label className="text-xs font-semibold text-slate-700">State *</Label>
              <Input value={f.state} onChange={(e) => set("state", e.target.value)} placeholder="CA" data-testid="license-state" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold text-slate-700">License Number</Label>
              <Input value={f.license_number} onChange={(e) => set("license_number", e.target.value)} data-testid="license-number" /></div>
            <div><Label className="text-xs font-semibold text-slate-700">License Type</Label>
              <Input value={f.license_type} onChange={(e) => set("license_type", e.target.value)} placeholder="Individual / Business" data-testid="license-type" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold text-slate-700">Residency</Label>
              <Select value={f.resident || "__none__"} onValueChange={(v) => set("resident", v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="license-resident"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="__none__">—</SelectItem>
                  <SelectItem value="resident">Resident</SelectItem><SelectItem value="non_resident">Non-Resident</SelectItem></SelectContent></Select></div>
            <div><Label className="text-xs font-semibold text-slate-700">Status</Label>
              <Select value={f.status || "active"} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="license-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{["active", "inactive", "expired", "pending"].map((s) => <SelectItem key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold text-slate-700">Issue / Effective Date</Label>
              <Input type="date" value={(f.issue_date || "").slice(0, 10)} onChange={(e) => set("issue_date", e.target.value)} data-testid="license-issue" /></div>
            <div><Label className="text-xs font-semibold text-slate-700">Expiration Date</Label>
              <Input type="date" value={(f.expiration || "").slice(0, 10)} onChange={(e) => set("expiration", e.target.value)} data-testid="license-expiration" /></div>
          </div>
          <div><Label className="text-xs font-semibold text-slate-700">Verification Status</Label>
            <Select value={f.verification_status} onValueChange={(v) => set("verification_status", v)}>
              <SelectTrigger data-testid="license-verification"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="unverified">Unverified</SelectItem>
                <SelectItem value="verified">Verified</SelectItem><SelectItem value="rejected">Not Accepted</SelectItem></SelectContent></Select></div>
          <div><Label className="text-xs font-semibold text-slate-700">Notes</Label>
            <Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="license-notes" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="license-save" className="bg-brand hover:bg-brand-strong">{mode === "add" ? "Add License" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function OpportunitiesPanel({ opps, personId, navigate, reload }) {
  const [creating, setCreating] = useState(false);
  const create = async () => {
    setCreating(true);
    try { const { data } = await api.post("/opportunities", { person_id: personId }); toast.success("Opportunity created"); navigate(`/recruiting/opportunity/${data.id}`); }
    catch (e) { toast.error(apiError(e)); } finally { setCreating(false); }
  };
  return (
    <PanelCard title="Recruiting Opportunities" testId="panel-opportunities"
      action={<Button size="sm" onClick={create} disabled={creating} data-testid="person-new-opportunity" className="bg-brand hover:bg-brand-strong"><Plus className="w-4 h-4 mr-1" /> New</Button>}>
      {opps.length ? (
        <div className="space-y-2" data-testid="person-opportunities">
          {opps.map((o) => (
            <button key={o.id} onClick={() => navigate(`/recruiting/opportunity/${o.id}`)} data-testid={`person-opp-${o.id}`}
              className="w-full text-left flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2.5 hover:border-brand/40 hover:bg-slate-50 transition-colors">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-700 truncate">{o.human_id} · {o.candidate_type || "Unknown type"}</p>
                <p className="text-xs text-slate-400 truncate">{o.current_stage_name} · {o.organization_name || "—"}</p>
              </div>
              <StatusBadge status={o.status} />
            </button>
          ))}
        </div>
      ) : <EmptyState icon={Briefcase} title="No opportunities" description="Start a recruiting opportunity for this person." />}
    </PanelCard>
  );
}

function NotesPanel({ personId, items, reload, who = "Person" }) {
  const [text, setText] = useState(""); const [iv, setIv] = useState("group"); const [pv, setPv] = useState("internal_only");
  const add = async () => { if (!text) return; try { await api.post("/notes", { person_id: personId, text, internal_visibility: iv, portal_visibility: pv, visibility: iv === "private" ? "private" : "recruiting_team" }); setText(""); setPv("internal_only"); reload(); toast.success("Note added"); } catch (e) { toast.error(apiError(e)); } };
  const change = async (n, patch) => { try { await api.patch(`/notes/${n.id}`, patch); reload(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <PanelCard title="Notes" testId="panel-notes">
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 mb-4">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a note…" data-testid="note-input" />
        <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
          <VisibilityControls internal={iv} portal={pv} onInternal={setIv} onPortal={setPv} who={who} testPrefix="note-vis" />
          <Button onClick={add} data-testid="note-submit" className="bg-brand hover:bg-brand-strong">Add Note</Button>
        </div>
      </div>
      {items.length ? items.map((n) => (
        <div key={n.id} className="rounded-lg border border-slate-200 bg-white p-4 mb-2" data-testid={`note-${n.id}`}>
          <div className="flex items-center justify-between"><div className="flex items-center gap-2"><UserAvatar name={n.author_name} size={24} /><span className="text-sm font-medium text-slate-700">{n.author_name}</span><VisibilityBadges item={n} who={who} testId={`note-badges-${n.id}`} /></div>
            <span className="text-xs text-slate-400 flex items-center gap-2">{n.can_edit !== false && (<>
              <Select value={n.internal_visibility || "group"} onValueChange={(v) => change(n, { internal_visibility: v })}>
                <SelectTrigger className="h-7 w-28 text-xs" data-testid={`note-internal-${n.id}`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="group">Group</SelectItem><SelectItem value="organization">Organization</SelectItem><SelectItem value="private">Private</SelectItem></SelectContent></Select>
              <button className="text-brand hover:underline" data-testid={`note-share-toggle-${n.id}`}
                onClick={() => change(n, { portal_visibility: n.portal_visibility === "shared_with_person" ? "internal_only" : "shared_with_person" })}>
                {n.portal_visibility === "shared_with_person" ? "Make internal" : `Share with ${who}`}</button></>)}
              {new Date(n.created_at).toLocaleString()}</span></div>
          <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{n.text}</p>
        </div>
      )) : <EmptyState icon={StickyNote} title="No notes" description="Add the first note." />}
    </PanelCard>
  );
}

function ActivitiesPanel({ personId, items, reload }) {
  const [f, setF] = useState({ type: "Phone Call", subject: "", description: "" });
  const [date, setDate] = useState(() => new Date());
  const [calOpen, setCalOpen] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const [ivStatus, setIvStatus] = useState("completed");
  const [matchList, setMatchList] = useState(null);
  const add = async (link = {}) => {
    try {
      await api.post("/activities", { type: f.type, subject: f.subject, ...descriptionPayload(f.description), person_id: personId, activity_at: (date || new Date()).toISOString(),
        ...(f.type === "Interview" ? { meeting_status: ivStatus, ...link } : {}) });
      setMatchList(null);
      setF({ type: "Phone Call", subject: "", description: "" }); setDate(new Date()); setFormKey((k) => k + 1);
      reload(); toast.success("Activity logged");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "MEETING_MATCHES") { setMatchList(d.matches); return; }
      toast.error(apiError(e));
    }
  };
  return (
    <PanelCard title="Activities" testId="panel-activities">
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 mb-4 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}><SelectTrigger data-testid="activity-type"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{["Phone Call", "Meeting", "Interview", "Text Conversation", "Email Conversation", "Networking Interaction", "Referral Contact", "Other"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
          <Popover open={calOpen} onOpenChange={setCalOpen}>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" className="justify-start font-normal text-slate-700" data-testid="activity-date-trigger">
                <CalendarDays className="w-4 h-4 mr-2 text-slate-400" />{(date || new Date()).toLocaleDateString()}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-white" align="start" data-testid="activity-date-calendar">
              <Calendar mode="single" selected={date} onSelect={(d) => { setDate(d || new Date()); setCalOpen(false); }} disabled={(d) => d > new Date()} initialFocus />
            </PopoverContent>
          </Popover>
        </div>
        <Input placeholder="Subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="activity-subject" />
        <ActivityDescriptionEditor key={formKey} value={f.description} onChange={(h) => setF((s) => ({ ...s, description: h }))} />
        {f.type === "Interview" && <div className="flex items-center gap-2 text-xs text-slate-600" data-testid="activity-interview-status">Interview status
          <select value={ivStatus} onChange={(e) => setIvStatus(e.target.value)} className="h-8 rounded-md border border-slate-200 px-2" data-testid="activity-interview-status-select">
            <option value="completed">Completed</option><option value="no_show">No-show</option><option value="scheduled">Scheduled (not yet held)</option></select></div>}
        {matchList && <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs space-y-1.5" data-testid="activity-match-list">
          <p className="font-semibold text-amber-900">Is this one of these existing meetings?</p>
          {matchList.map((m) => <button key={m.id} onClick={() => add({ appointment_id: m.id })} className="block w-full text-left rounded border border-amber-200 bg-white px-2 py-1.5 hover:border-amber-400" data-testid={`activity-link-${m.id}`}>{m.title} · {m.start ? new Date(m.start).toLocaleString() : ""} · {m.status.replace("_", " ")}</button>)}
          <button onClick={() => add({ separate_meeting: true })} className="text-[#006090] underline" data-testid="activity-separate-meeting">No — this is a separate interview</button></div>}
        <div className="flex justify-end"><Button onClick={() => add()} data-testid="activity-submit" className="bg-brand hover:bg-brand-strong">Log Activity</Button></div>
      </div>
      {items.length ? items.map((a) => (
        <ActivityItem key={a.id} a={a} onSaved={reload}>
          {a.meeting_doc_id && (
            <button type="button" onClick={() => setViewDoc(a.meeting_doc_id)} data-testid={`activity-view-meeting-${a.id}`}
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline">
              <CalendarDays className="w-3.5 h-3.5" /> View Meeting Notes
            </button>
          )}
        </ActivityItem>
      )) : <EmptyState icon={ActIcon} title="No activities" description="Log calls, meetings and interviews here." />}
      {viewDoc && <MeetingDocDialog docId={viewDoc} open onClose={() => setViewDoc(null)} onChanged={reload} />}
    </PanelCard>
  );
}

function AttachmentsPanel({ personId, items, canManage, canUpload, onChanged, who = "Person" }) {
  const [reassign, setReassign] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editMeta, setEditMeta] = useState(null);
  const [viewerDoc, setViewerDoc] = useState(null);
  const openFile = (a) => setViewerDoc(a);
  const openName = (a) => (["pdf", "docx"].includes(viewableKind(a.original_filename, a.mime_type))
    ? openDocumentPreview(`/attachments/${a.id}/file`, a.original_filename) : setViewerDoc(a));
  const downloadFile = async (a) => {
    try { await downloadAttachment(`/attachments/${a.id}/file`, a.original_filename); }
    catch (e) { toast.error(apiError(e, "You do not have permission to download this file.")); }
  };
  const [shareBusy, setShareBusy] = useState(null);
  const toggleShare = async (a, on) => {
    setShareBusy(a.id);
    try { await api.patch(`/attachments/${a.id}`, { portal_visibility: on ? "shared_with_person" : "internal_only" }); toast.success(on ? `Shared with ${who}` : `No longer shared with ${who}`); onChanged && onChanged(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setShareBusy(null); }
  };
  const archive = async (a) => {
    try { await api.patch(`/attachments/${a.id}`, { archived: true }); toast.success("Attachment archived"); onChanged && onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <PanelCard title="Attachments" testId="panel-attachments"
      action={canUpload ? <Button size="sm" onClick={() => setUploadOpen(true)} data-testid="attachment-add" className="bg-brand hover:bg-brand-strong"><Plus className="w-4 h-4 mr-1" /> Add Attachment</Button> : null}>
      {items.length ? (
        <div className="overflow-x-auto tr-scroll">
          <table className="w-full text-sm" data-testid="attachments-table">
            <thead><tr className="text-left text-xs font-semibold text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3">File Name</th><th className="py-2 pr-3">Category</th><th className="py-2 pr-3">Description</th>
              <th className="py-2 pr-3">Document Date</th><th className="py-2 pr-3">Expires</th><th className="py-2 pr-3">Type</th>
              <th className="py-2 pr-3">Visibility</th><th className="py-2 pr-3 whitespace-nowrap">Share with {who}</th><th className="py-2 pr-3"></th></tr></thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-b border-slate-100" data-testid={`attachment-${a.id}`}>
                  <td className="py-2 pr-3 text-slate-800">
                    <button onClick={() => openName(a)} data-testid={`attachment-name-${a.id}`}
                      className="text-left text-brand hover:underline font-medium">{a.original_filename}</button>
                  </td>
                  <td className="py-2 pr-3 text-slate-600">{a.category || "—"}</td>
                  <td className="py-2 pr-3 text-slate-600 max-w-xs truncate" title={a.description || ""}>{a.description || "—"}</td>
                  <td className="py-2 pr-3 text-slate-600">{a.document_date ? new Date(a.document_date).toLocaleDateString() : "—"}</td>
                  <td className="py-2 pr-3 text-slate-600">{a.expiration_date ? new Date(a.expiration_date).toLocaleDateString() : "—"}</td>
                  <td className="py-2 pr-3 text-slate-600">{a.mime_type || "—"}</td>
                  <td className="py-2 pr-3 whitespace-nowrap"><VisibilityBadges item={a} who={who} testId={`attachment-badges-${a.id}`} /></td>
                  <td className="py-2 pr-3">
                    <Switch checked={a.portal_visibility === "shared_with_person"} disabled={!(canManage || canUpload) || shareBusy === a.id}
                      onCheckedChange={(v) => toggleShare(a, v)} aria-label={`Share with ${who}`} data-testid={`attachment-share-toggle-${a.id}`} />
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => openFile(a)} data-testid={`attachment-view-${a.id}`}>
                      <Eye className="w-3.5 h-3.5 mr-1" /> View
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => downloadFile(a)} data-testid={`attachment-download-${a.id}`} className="ml-1">
                      <Download className="w-3.5 h-3.5 mr-1" /> Download
                    </Button>
                    {(canManage || canUpload) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8 ml-1" data-testid={`attachment-menu-${a.id}`}><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-white">
                          <DropdownMenuItem onClick={() => setEditMeta(a)} data-testid={`attachment-edit-${a.id}`}>Edit Metadata</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => archive(a)} data-testid={`attachment-archive-${a.id}`}>Archive</DropdownMenuItem>
                          {canManage && <DropdownMenuItem onClick={() => setReassign(a)} data-testid={`attachment-reassign-${a.id}`}>Reassign</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <EmptyState icon={Paperclip} title="No attachments" description="Add a document to this person or import it during migration." />}
      {canManage && <ReassignDialog item={reassign} onClose={() => setReassign(null)} onDone={() => { setReassign(null); onChanged && onChanged(); }} />}
      {uploadOpen && <AttachmentUploadDialog personId={personId} who={who} onClose={() => setUploadOpen(false)} onDone={() => { setUploadOpen(false); onChanged && onChanged(); }} />}
      {editMeta && <AttachmentEditDialog attachment={editMeta} onClose={() => setEditMeta(null)} onDone={() => { setEditMeta(null); onChanged && onChanged(); }} />}
      <DocumentViewer attachment={viewerDoc} open={!!viewerDoc} onClose={() => setViewerDoc(null)} />
    </PanelCard>
  );
}

function AttachmentUploadDialog({ personId, onClose, onDone, who = "Person" }) {
  const [file, setFile] = useState(null);
  const [cats, setCats] = useState([]);
  const [f, setF] = useState({ category: "", description: "", document_date: "", expiration_date: "", internal_visibility: "group", portal_visibility: "internal_only" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/document-categories").then((r) => setCats(r.data.items || [])).catch(() => {}); }, []);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!file) return toast.error("Choose a file to upload");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (f.category) fd.append("category", f.category);
      if (f.description) fd.append("description", f.description);
      if (f.document_date) fd.append("document_date", f.document_date);
      if (f.expiration_date) fd.append("expiration_date", f.expiration_date);
      fd.append("internal_visibility", f.internal_visibility);
      fd.append("portal_visibility", f.portal_visibility);
      const { data } = await api.post(`/persons/${personId}/attachments`, fd);
      if (data.duplicate) toast.warning("This file is already attached to this person.");
      else toast.success("Attachment uploaded");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="attachment-upload-dialog">
        <DialogHeader><DialogTitle>Add Attachment</DialogTitle><DialogDescription>Upload a document to this person's record.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs font-semibold text-slate-700">File *</Label>
            <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="attachment-file-input" /></div>
          <div><Label className="text-xs font-semibold text-slate-700">Document Category</Label>
              <Select value={f.category || "__none__"} onValueChange={(v) => set("category", v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="attachment-category"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="bg-white max-h-64"><SelectItem value="__none__">None</SelectItem>
                  {cats.map((c) => <SelectItem key={c.id} value={c.label}>{c.label}</SelectItem>)}</SelectContent></Select></div>
          <VisibilityControls internal={f.internal_visibility} portal={f.portal_visibility} onInternal={(v) => set("internal_visibility", v)}
            onPortal={(v) => set("portal_visibility", v)} who={who} testPrefix="attachment-vis" />
          <div><Label className="text-xs font-semibold text-slate-700">Description / Notes</Label>
            <Textarea rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="attachment-description" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold text-slate-700">Document Date</Label>
              <Input type="date" value={f.document_date} onChange={(e) => set("document_date", e.target.value)} data-testid="attachment-doc-date" /></div>
            <div><Label className="text-xs font-semibold text-slate-700">Expiration Date</Label>
              <Input type="date" value={f.expiration_date} onChange={(e) => set("expiration_date", e.target.value)} data-testid="attachment-exp-date" /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !file} data-testid="attachment-upload-submit" className="bg-brand hover:bg-brand-strong">Upload</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AttachmentEditDialog({ attachment, onClose, onDone }) {
  const [cats, setCats] = useState([]);
  const [f, setF] = useState({ category: attachment.category || "", description: attachment.description || "",
    document_date: (attachment.document_date || "").slice(0, 10), expiration_date: (attachment.expiration_date || "").slice(0, 10),
    internal_visibility: attachment.internal_visibility || "group", portal_visibility: attachment.portal_visibility || "internal_only" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/document-categories").then((r) => setCats(r.data.items || [])).catch(() => {}); }, []);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    setSaving(true);
    try {
      await api.patch(`/attachments/${attachment.id}`, { category: f.category || null, description: f.description || null,
        document_date: f.document_date || null, expiration_date: f.expiration_date || null,
        internal_visibility: f.internal_visibility, portal_visibility: f.portal_visibility });
      toast.success("Attachment updated"); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="attachment-edit-dialog">
        <DialogHeader><DialogTitle>Edit Attachment</DialogTitle><DialogDescription>{attachment.original_filename}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs font-semibold text-slate-700">Category</Label>
              <Select value={f.category || "__none__"} onValueChange={(v) => set("category", v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="attachment-edit-category"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="bg-white max-h-64"><SelectItem value="__none__">None</SelectItem>
                  {cats.map((c) => <SelectItem key={c.id} value={c.label}>{c.label}</SelectItem>)}</SelectContent></Select></div>
          <VisibilityControls internal={f.internal_visibility} portal={f.portal_visibility} onInternal={(v) => set("internal_visibility", v)}
            onPortal={(v) => set("portal_visibility", v)} testPrefix="attachment-edit-vis" />
          <div><Label className="text-xs font-semibold text-slate-700">Description</Label>
            <Textarea rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="attachment-edit-description" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold text-slate-700">Document Date</Label>
              <Input type="date" value={f.document_date} onChange={(e) => set("document_date", e.target.value)} data-testid="attachment-edit-doc-date" /></div>
            <div><Label className="text-xs font-semibold text-slate-700">Expiration Date</Label>
              <Input type="date" value={f.expiration_date} onChange={(e) => set("expiration_date", e.target.value)} data-testid="attachment-edit-exp-date" /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="attachment-edit-submit" className="bg-brand hover:bg-brand-strong">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReassignDialog({ item, onClose, onDone }) {
  const [person, setPerson] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPerson(null); setReason(""); }, [item]);
  if (!item) return null;
  const submit = async () => {
    if (!person) return toast.error("Select the correct Person");
    setBusy(true);
    try {
      await api.post(`/migration/attachments/${item.id}/reassign`, { person_id: person.id, reason: reason || null });
      toast.success(`Reassigned to ${person.name}`);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="reassign-dialog">
        <DialogHeader><DialogTitle>Reassign Attachment</DialogTitle>
          <DialogDescription>Move “{item.original_filename}” to the correct Person. Provenance is preserved and the change is audited.</DialogDescription></DialogHeader>
        <PersonSearchSelect onSelect={setPerson} selected={person} testId="reassign-person-search" />
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for reassignment (optional)" data-testid="reassign-reason" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !person} data-testid="reassign-confirm" className="bg-[#006090] hover:bg-[#004E75]">Confirm Reassign</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ListsPanel({ lists }) {
  const all = [...(lists.manual || []).map((l) => ({ ...l, kind: "manual" })), ...(lists.smart || []).map((l) => ({ ...l, kind: "smart" }))];
  return (
    <PanelCard title="Lists" testId="panel-lists">
      {all.length ? (
        <div className="flex flex-wrap gap-2" data-testid="person-lists">
          {all.map((l) => <span key={l.id} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm text-slate-700" data-testid={`person-list-${l.id}`}>{l.name}<span className="text-[10px] uppercase text-slate-400">{l.kind}</span></span>)}
        </div>
      ) : <EmptyState icon={Tag} title="Not in any list" description="Add this person to a list from the Lists page." />}
    </PanelCard>
  );
}

function ActivitiesWithTimeline({ activities, timeline }) {
  const [mode, setMode] = useState("list");
  return (
    <div className="space-y-3">
      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" role="tablist" data-testid="activities-mode">
        {[["list", "Logged Activities"], ["timeline", "Timeline (all events)"]].map(([k, l]) => (
          <button key={k} role="tab" aria-selected={mode === k} onClick={() => setMode(k)} data-testid={`activities-mode-${k}`}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${mode === k ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
        ))}
      </div>
      {mode === "list" ? activities : timeline}
    </div>
  );
}

function RecordHistoryPanel({ personId }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/audit-log", { params: { record_id: personId, page_size: 100 } }).then((r) => setItems(r.data.entries || r.data.items || [])).catch(() => setItems([]));
  }, [personId]);
  if (items === null) return <p className="text-sm text-slate-400">Loading…</p>;
  if (!items.length) return <EmptyState icon={History} title="No audit entries" description="Changes made to this record will appear here." />;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="record-history">
      <h3 className="font-display font-semibold text-sm mb-3">Record History / Audit Log</h3>
      <ul className="divide-y divide-slate-100">
        {items.map((e, i) => (
          <li key={e.id || i} className="py-2 text-sm flex flex-wrap justify-between gap-2">
            <span className="text-slate-700">{(e.action || "").replace(/_/g, " ")}</span>
            <span className="text-xs text-slate-400">{e.actor_name || "System"} · {e.timestamp ? new Date(e.timestamp).toLocaleString() : ""}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TimelinePanel({ events }) {
  const navigate = useNavigate();
  if (!events?.length) return <EmptyState icon={Clock} title="No timeline events" description="Activity will appear here." />;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-3" data-testid="person-timeline">
      {events.map((e) => {
        const isSupport = e.type === "support_conversation" && e.related?.support_conversation_id;
        return (
          <div key={e.id} className="flex gap-3">
            <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${isSupport ? "bg-[#0D9488]" : "bg-brand"}`} />
            <div className="flex-1 border-b border-slate-100 pb-3">
              <p className="text-sm text-slate-700">{e.description}</p>
              {(e.detail_html || e.detail_text) && <div className="mt-1 rounded-md bg-slate-50 px-3 py-2" data-testid={`timeline-detail-${e.id}`}><ActivityDescription html={e.detail_html} text={e.detail_text} /></div>}
              <p className="text-xs text-slate-400">{e.user_name} · {e.created_at ? new Date(e.created_at).toLocaleString() : ""}</p>
              {e.email && <EmailStatusLine c={e.email} />}
              {e.email && (e.email.direction === "inbound" || e.email.replied_at) && <ViewConversationButton commId={e.email.id} />}
              {isSupport && (
                <button data-testid={`timeline-view-conversation-${e.related.support_conversation_id}`}
                  onClick={() => navigate(`/communications?chat=${e.related.support_conversation_id}`)}
                  className="mt-1 text-xs text-brand font-medium hover:underline">Open Thread →</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AddNoteDialog({ personId, onClose, onDone }) {
  const [text, setText] = useState(""); const [iv, setIv] = useState("group"); const [pv, setPv] = useState("internal_only"); const [saving, setSaving] = useState(false);
  const submit = async () => { if (!text.trim()) return; setSaving(true); try { await api.post("/notes", { person_id: personId, text: text.trim(), internal_visibility: iv, portal_visibility: pv, visibility: iv === "private" ? "private" : "recruiting_team" }); toast.success("Note added"); onDone(); } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="add-note-dialog">
        <DialogHeader><DialogTitle>Add Note</DialogTitle><DialogDescription>Add a note linked to this person.</DialogDescription></DialogHeader>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a note…" data-testid="dialog-note-input" />
        <VisibilityControls internal={iv} portal={pv} onInternal={setIv} onPortal={setPv} testPrefix="dialog-note-vis" />
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="dialog-note-submit" className="bg-brand hover:bg-brand-strong">Add Note</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

